import { Injectable } from '@angular/core';
import type { Account } from '@omni/classes/account/account.class';
import type { Contact } from '@omni/classes/contact/contact.class';
import type { Brand } from '@omni/classes/brand/brand.class';
import { map } from 'rxjs';
import { DiskService } from '../disk/disk.service';
import { DB_KEY_PREFIXES } from '@omni/config/pouch-db.config';
import { DeltaService, EntityNames } from '@omni/data-services/delta/delta.service';
import { DynamicsClientService } from '@omni/data-services/dynamics-client/dynamics-client.service';
import { AuthenticationService } from '../authentication.service';
import { FeatureActionsMap } from '@omni/classes/authentication/user.class';
import { BrandOfflineService } from '../brand/brand.service';
import { AccountOfflineService } from '../account/account.offline.service';
import { ContactOfflineService } from '../contact/contact.service';
import {
  Period,
  Metric,
  RxSalesData,
  BaseSalesData,
  SalesMetrics,
  RxMetrics
} from './rxsalesdata.types';
import { OfflineServiceBase } from '../offline/offline.service';
import { DateTimeFormatsService } from '../date-time-formats/date-time-formats.service';

/**
 * Service for managing Rx Sales Data synchronization and aggregation.
 * Extends OfflineServiceBase to provide standardized offline data management.
 * Handles data fetching from Dynamics CRM, local storage, and provides
 * filtered observables for different data types (rx, brick, direct sales).
 * 
 * This service manages prescription and sales data across different target types:
 * - Contact-based prescription data (TRx, NRx, Refills)
 * - Account-based direct sales data (SalesVolume, SalesValue)
 * - Brick-based territory sales data (SalesVolume, SalesValue)
 */

/**
 * Raw data structure for Rx Sales Data attributes as received from Dynamics CRM.
 * Contains all the necessary fields for processing and aggregating sales data.
 */
type RawRxSalesDataAttribute = {
  _id?: string; // Document ID
  _rev?: string; // Document revision
  indskr_rxsalesdataattributeid: string; // Rx Sales Data Attribute ID
  indskr_changefromlastperiod: number;
  indskr_metric: Metric; // Metric type (TRx, NRx, Refills, SalesValue, SalesVolume)
  indskr_period: Period; // Time period (CW, C3W, C6W, CM, C3M, C6M, QTD, YTD)
  indskr_value: number; // Metric value
  statecode: number; // Active = 0, Inactive = 1
  ['rxsalesdata.indskr_rxsalesdataid']: string; // Rx Sales Data ID
  ['rxsalesdata.indskr_product']: string; // Product ID
  ['rxsalesdata.indskr_product_Formatted']: string; // Product name
  ['rxsalesdata.indskr_product_LogicalName']: 'product'; // Product logical name
  ['rxsalesdata.indskr_target']: string; // Target ID
  ['rxsalesdata.indskr_target_Formatted']: string; // Target name
  ['rxsalesdata.indskr_target_LogicalName']: 'contact' | 'account' | 'omnione_brick'; // Target logical name
  ['rxsalesdata.statecode']: number; // State code
}


@Injectable({
  providedIn: 'root'
})
export class RxSalesDataService extends OfflineServiceBase<RawRxSalesDataAttribute, 'indskr_rxsalesdataattributeid'> {
  /** Observable that reads raw Rx Sales Data from local disk storage */
  readonly #rawRxSalesData$ = this.raw$;

  /** Observable that provides aggregated Rx Sales Data with all target types */
  readonly rxSalesData$ = this.#rawRxSalesData$
    .pipe(
      map((data) => this.#aggregateRxSalesData(data))
    );

  /** Observable filtered to show only contact-based prescription data (rx type) */
  readonly rxData$ = this.rxSalesData$
    .pipe(
      map((data) => data.filter((item) => item.type === 'rx'))
    );

  /** Observable filtered to show only brick-based sales data */
  readonly brickSalesData$ = this.rxSalesData$
    .pipe(
      map((data) => data.filter((item) => item.type === 'brick'))
    );

  /** Observable filtered to show only account-based direct sales data */
  readonly directSalesData$ = this.rxSalesData$
    .pipe(
      map((data) => data.filter((item) => item.type === 'direct'))
    );

  /**
   * Initializes the RxSalesDataService with required dependencies.
   * @param disk - Disk service for local storage operations
   * @param deltaService - Delta service for sync state management
   * @param dynamics - Dynamics CRM client for data fetching
   * @param authService - Authentication service for user position data
   * @param accountService - Account service for account data resolution
   * @param contactService - Contact service for contact data resolution
   * @param brandService - Brand service for product data resolution
   */
  constructor(
    disk: DiskService,
    deltaService: DeltaService,
    private readonly dynamics: DynamicsClientService,
    private readonly authService: AuthenticationService,
    private readonly accountService: AccountOfflineService,
    private readonly contactService: ContactOfflineService,
    private readonly brandService: BrandOfflineService,
    dateTimeFormatsService: DateTimeFormatsService,
  ) {
    super(disk, deltaService, EntityNames.rxsalesdata, 'indskr_rxsalesdataattributeid', DB_KEY_PREFIXES.RX_SALES_DATA, dateTimeFormatsService);
    // Expose service to window for debugging purposes
    (window as any).rxSvc = this;
  }

  protected override isSyncAllowed(): boolean {
    return this.authService.hasFeatureAction(FeatureActionsMap.CONTACT_SPOTLIGHT);
  }

  /**
   * Fetches Rx Sales Data from Dynamics CRM based on user positions and last updated time.
   * Implements the fetchData method required by OfflineServiceBase.
   * @param lastUpdatedTime - ISO string timestamp for incremental sync filtering
   * @returns Promise<RawRxSalesDataAttribute[]> Array of processed sales data attributes
   */
  public async fetchData(lastUpdatedTime: string) {
    // Get user position IDs for filtering data based on user's access
    const positionIDs = this.authService.user.positions.map((o) => o.ID);

    // Build position condition for FetchXML query
    const positionCondition = positionIDs.length > 1 ? positionIDs.map(id => `<value>${id}</value>`).join('') : (positionIDs[0] ?? null);

    // Helper function to create position filter conditions for FetchXML
    const positionFilter = (alias: string, attribute: string) =>
      positionIDs.length > 1
        ? `<condition entityname="${alias}" attribute="${attribute}" operator="in">${positionCondition}</condition>`
        : `<condition entityname="${alias}" attribute="${attribute}" operator="eq" value="${positionCondition}" />`;

    // Build FetchXML query to retrieve Rx Sales Data attributes from Dynamics CRM
    // Includes joins to account, contact, and brick entities with position-based filtering
    const fetchXML = [
      '<fetch version="1.0" output-format="xml-platform" mapping="logical" no-lock="true">',
      '<entity name="indskr_rxsalesdataattribute">',
      '<attribute name="indskr_rxsalesdataattributeid" />',
      '<attribute name="indskr_changefromlastperiod" />',
      '<attribute name="indskr_metric" />',
      '<attribute name="indskr_period" />',
      '<attribute name="indskr_value" />',
      '<attribute name="statecode" />',
      '<filter type="and">',
      '<condition entityname="rxsalesdata" attribute="indskr_rxsalesdataid" operator="not-null" />',
      ...(lastUpdatedTime ? [
        `<condition attribute="modifiedon" operator="ge" value="` + lastUpdatedTime + `" />`
      ] : [
        '<condition attribute="statecode" operator="eq" value="0" />',
        '<condition entityname="rxsalesdata" attribute="statecode" operator="eq" value="0" />',
      ]),
      '<filter type="or">',
      positionFilter('ap', 'positionid'),
      positionFilter('ap', 'parentpositionid'),
      positionFilter('cp', 'positionid'),
      positionFilter('cp', 'parentpositionid'),
      positionFilter('bp', 'positionid'),
      positionFilter('bp', 'parentpositionid'),
      '</filter>',
      '</filter>',
      '<link-entity name="indskr_rxsalesdata" from="indskr_rxsalesdataid" to="indskr_rxsalesdata" link-type="outer" alias="rxsalesdata">',
      '<attribute name="indskr_product" />',
      '<attribute name="indskr_target" />',
      '<attribute name="statecode" />',
      '<link-entity name="account" from="accountid" to="indskr_target" link-type="outer">',
      '<link-entity name="indskr_customerposition" from="indskr_customerid" to="accountid" link-type="outer" >',
      '<link-entity name="position" from="positionid" to="indskr_positionid" link-type="outer" alias="ap" />',
      '</link-entity>',
      '</link-entity>',
      '<link-entity name="contact" from="contactid" to="indskr_target" link-type="outer">',
      '<link-entity name="indskr_customerposition" from="indskr_customerid" to="contactid" link-type="outer" >',
      '<link-entity name="position" from="positionid" to="indskr_positionid" link-type="outer" alias="cp" />',
      '</link-entity>',
      '</link-entity>',
      '<link-entity name="omnione_brick" from="omnione_brickid" to="indskr_target" link-type="outer">',
      '<link-entity name="omnione_brickposition" from="omnione_brick" to="omnione_brickid" link-type="outer" >',
      '<link-entity name="position" from="positionid" to="omnione_position" link-type="outer" alias="bp" />',
      '</link-entity>',
      '</link-entity>',
      '</link-entity>',
      '</entity>',
      '</fetch>'
    ].join('');

    // Execute FetchXML query against Dynamics CRM
    const response = await this.dynamics.executeFetchQuery<RawRxSalesDataAttribute>('indskr_rxsalesdataattributes', fetchXML);
    let rxSalesDataAttributes = response;

    // Helper function to check for null/undefined values
    const isNullOrUndefined = (value: any) => value === undefined || value === null;

    // Process each sales data attribute and prepare for local storage
    const data = (await Promise.all(rxSalesDataAttributes.map(async item => {
      // Mark as deleted if inactive or has no meaningful data
      if (item.statecode !== 0 || (isNullOrUndefined(item.indskr_changefromlastperiod) && isNullOrUndefined(item.indskr_value))) {
        return {
          indskr_rxsalesdataattributeid: item.indskr_rxsalesdataattributeid,
          _deleted: true,
        };
      }
      return item;
    }))).filter(Boolean);

    return data;
  }

  /**
   * Aggregates raw Rx Sales Data attributes into structured RxSalesData objects.
   * Groups data by target type, target ID, product, and period, then aggregates metrics.
   * @param data - Array of raw sales data attributes from CRM
   * @returns Array of aggregated RxSalesData objects
   */
  #aggregateRxSalesData(data: RawRxSalesDataAttribute[]) {
    const group = new Map<string, RxSalesData>();

    for (const item of data) {
      // Extract product information and resolve brand data
      const productId = item['rxsalesdata.indskr_product'];
      const productName = item['rxsalesdata.indskr_product_Formatted'];
      const period = item.indskr_period;
      const product = this.brandService.getBrandByID(productId) ?? { ID: productId, name: productName } as Brand;

      // Create base data structure shared across all target types
      const baseData = { product, period };

      // Extract target information for grouping and processing
      const targetType = item['rxsalesdata.indskr_target_LogicalName'];
      const targetId = item['rxsalesdata.indskr_target'];
      const targetName = item['rxsalesdata.indskr_target_Formatted'];

      // Create unique key for grouping: targetType:targetId:productId:period
      const key = `${targetType}:${targetId}:${productId}:${period}`;

      // Process item based on its target type
      this.#processItemByTargetType(group, item, baseData, targetType, targetId, targetName, key);
    }

    return Array.from(group.values()).filter(Boolean);
  }

  /**
   * Processes an item based on its target type (contact, account, or brick).
   * Routes to appropriate processor based on the target type.
   * @param group - Map to store aggregated data
   * @param item - Raw sales data attribute
   * @param baseData - Base data for the item
   * @param targetType - Type of target (contact, account, omnione_brick)
   * @param targetId - ID of the target
   * @param targetName - Name of the target
   * @param key - Unique key for grouping
   */
  #processItemByTargetType(
    group: Map<string, RxSalesData>,
    item: RawRxSalesDataAttribute,
    baseData: BaseSalesData,
    targetType: string,
    targetId: string,
    targetName: string,
    key: string
  ) {
    switch (targetType) {
      case 'contact':
        this.#processContactData(group, item, baseData, targetId, targetName, key);
        break;
      case 'account':
        this.#processAccountData(group, item, baseData, targetId, targetName, key);
        break;
      case 'omnione_brick':
        this.#processBrickData(group, item, baseData, targetId, targetName, key);
        break;
    }
  }

  /**
   * Processes contact-based prescription data (rx type).
   * Creates or updates contact data with prescription metrics (TRx, NRx, Refills).
   * @param group - Map to store aggregated data
   * @param item - Raw sales data attribute
   * @param baseData - Base data for the item
   * @param targetId - Contact ID
   * @param targetName - Contact name
   * @param key - Unique key for grouping
   */
  #processContactData(
    group: Map<string, RxSalesData>,
    item: RawRxSalesDataAttribute,
    baseData: BaseSalesData,
    targetId: string,
    targetName: string,
    key: string
  ) {
    let data = group.get(key);
    if (!data) {
      // Create new contact data entry
      const contact = this.contactService.getContactByID(targetId);
      data = {
        ...baseData,
        type: 'rx',
        contact: contact ?? { ID: targetId, fullname: targetName } as Contact,
      };
    }

    // Validate that this is contact data, remove if not
    if (!('contact' in data)) {
      group.delete(key);
      return;
    }

    // Aggregate prescription metrics
    this.#aggregateRxMetrics(data, item);
    group.set(key, data);
  }

  /**
   * Processes account-based direct sales data.
   * Creates or updates account data with sales metrics (SalesVolume, SalesValue).
   * @param group - Map to store aggregated data
   * @param item - Raw sales data attribute
   * @param baseData - Base data for the item
   * @param targetId - Account ID
   * @param targetName - Account name
   * @param key - Unique key for grouping
   */
  #processAccountData(
    group: Map<string, RxSalesData>,
    item: RawRxSalesDataAttribute,
    baseData: BaseSalesData,
    targetId: string,
    targetName: string,
    key: string
  ) {
    let data = group.get(key);
    if (!data) {
      // Create new account data entry
      const account = this.accountService.getAccountById(targetId);
      data = {
        ...baseData,
        type: 'direct',
        account: account ?? { id: targetId, displayName: targetName, accountName: targetName } as Account,
      };
    }

    // Validate that this is account data, remove if not
    if (!('account' in data)) {
      group.delete(key);
      return;
    }

    // Aggregate sales metrics
    this.#aggregateSalesMetrics(data, item);
    group.set(key, data);
  }

  /**
   * Processes brick-based territory sales data.
   * Creates or updates brick data with sales metrics (SalesVolume, SalesValue).
   * @param group - Map to store aggregated data
   * @param item - Raw sales data attribute
   * @param baseData - Base data for the item
   * @param targetId - Brick ID
   * @param targetName - Brick name
   * @param key - Unique key for grouping
   */
  #processBrickData(
    group: Map<string, RxSalesData>,
    item: RawRxSalesDataAttribute,
    baseData: BaseSalesData,
    targetId: string,
    targetName: string,
    key: string
  ) {
    let data = group.get(key);
    if (!data) {
      // Create new brick data entry
      data = {
        ...baseData,
        type: 'brick',
        brick: { brickId: targetId, brickName: targetName },
      };
    }

    // Validate that this is brick data, remove if not
    if (!('brick' in data)) {
      group.delete(key);
      return;
    }

    // Aggregate sales metrics
    this.#aggregateSalesMetrics(data, item);
    group.set(key, data);
  }

  /**
   * Aggregates prescription-related metrics (TRx, NRx, Refills).
   * Updates the appropriate metric field based on the metric type.
   * @param data - RxMetrics object to update
   * @param item - Raw sales data attribute containing metric information
   */
  #aggregateRxMetrics(data: RxMetrics, item: RawRxSalesDataAttribute) {
    const metricData = { value: item.indskr_value, change: item.indskr_changefromlastperiod };

    switch (item.indskr_metric) {
      case Metric.TRx:
        data.TRx = metricData;
        break;
      case Metric.NRx:
        data.NRx = metricData;
        break;
      case Metric.Refills:
        data.Refills = metricData;
        break;
    }
  }

  /**
   * Aggregates sales-related metrics (SalesVolume, SalesValue).
   * Updates the appropriate sales metric field based on the metric type.
   * @param data - SalesMetrics object to update
   * @param item - Raw sales data attribute containing metric information
   */
  #aggregateSalesMetrics(data: SalesMetrics, item: RawRxSalesDataAttribute) {
    const metricData = { value: item.indskr_value, change: item.indskr_changefromlastperiod };

    switch (item.indskr_metric) {
      case Metric.SalesVolume:
        data.salesVolume = metricData;
        break;
      case Metric.SalesValue:
        data.salesValue = metricData;
        break;
    }
  }
}